import { ReactElement, useState } from "react";
import {Button,Col,TimePicker,Drawer,Form,Input,Row,Select,Upload,message} from "antd";
import { InboxOutlined, DeleteOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import firebase from "firebase/app";
import "firebase/storage";
import { DrawerFooter } from "../../components/GlobalStyles";
import { CREATE_BRANCH_REQUEST } from "../../redux/constants";

const { useForm } = Form;
const { Option } = Select;

type Props = {
  club: string;
  visible: boolean;
  onClose: () => void;
};

function beforeUpload(file: any) {
  const isJpgOrPng = file.type === "image/jpeg" || file.type === "image/png";
  if (!isJpgOrPng) {
    message.error("You can only upload JPG/PNG file!");
  }
  return false;
}

export function CreateBranchDrawer({
  club,
  visible,
  onClose,
}: Props): ReactElement {
  const dispatch = useDispatch();
  const [form] = useForm();
  const { t } = useTranslation();
  const [loading, setLoading] = useState<boolean>(false);
  const [femaleOnly, setFemaleOnly] = useState<any[]>([]);
  const [startTime, setStartTime] = useState<any>();
  const [endTime, setEndTime] = useState<any>();

  const storage = firebase.storage();

  const getBranchId = async (clubFirestoreRef: string) => {
    const lastBranchSnapshot = await firebase.firestore().collection("branches").where("club", "==", clubFirestoreRef).orderBy("branchId", "desc").limit(1).get();
    if(lastBranchSnapshot.size === 0 ) return "01";
    const { branchId } = lastBranchSnapshot.docs[0].data();
    const newBranchId = parseInt(branchId, 10) + 1;
    if(newBranchId < 10 ) return `0${newBranchId}`;
    return `${newBranchId}`;
  }

  const onFinish = async (values: any) => {
    setLoading(true);

    const newBranchId = await getBranchId(club);
    if(newBranchId === "NaN"){
      message.error("Something went wrong!");
      return;
    }
    const formValues = values;
    const availability = []
    availability.push(values.startTime.format("HH:mm"));
    availability.push(values.endTime.format("HH:mm"));
    formValues.availability = availability;
    if (values.photo) {
      const ref = `clubs/${values.name}/${values.photo.file.name}`;
      await storage.ref(ref).put(values.photo.file);
      const clubImageUrl = await storage.ref(ref).getDownloadURL();
      formValues.photo = clubImageUrl;
    } else {
      formValues.photo = "";
    }
    const branch = { club, branchId: newBranchId, ...formValues, femaleOnly, firestoreRef: newBranchId };
    dispatch({ type: CREATE_BRANCH_REQUEST, payload: branch });
    form.resetFields();
    setLoading(false);
    onClose();
  };

  const onFinishFailed = (errorInfo: any) => {
    // eslint-disable-next-line no-console
    console.log("Failed:", errorInfo);
  };

  const onAddFemaleDay = () => {
    const one = {
      day : "1",
      startTime: "",
      endTime: ""
    };
    const mFemaleDays = [...femaleOnly, one];
    setFemaleOnly(mFemaleDays);
  }

  const onChangeFemaleDay = (value: string, index: number) => {
    const mFemaleDays = [...femaleOnly];
    mFemaleDays[index].day = value;
    setFemaleOnly(mFemaleDays);
  }
  const onChangeFemaleDayStartTime = (value: any, index: number) => {
    const mFemaleDays = [...femaleOnly];
    mFemaleDays[index].startTime = value.format("HH:mm");
    setFemaleOnly(mFemaleDays);
  }
  const onChangeFemaleDayEndTime = (value: any, index: number) => {
    const mFemaleDays = [...femaleOnly];
    mFemaleDays[index].endTime = value.format("HH:mm");
    setFemaleOnly(mFemaleDays);
  }

  const disabledHoursForFemaleOnly = () => {
    if(!startTime || !endTime) return [];
    const startHour = startTime.hour();
    const endHour = endTime.hour();
    const disabledHours = [];
    if(startHour < endHour){
      for(let i = 0; i < 24; i+=1){
        if(i < startHour || i > endHour) disabledHours.push(i);
      }
    } else {
      for(let i = 0; i < 24; i+=1){
        if(i < startHour && i > endHour) disabledHours.push(i);
      }
    }
    return disabledHours;
  }
  const removeFemaleOnly = (index: number) => {
    const m = [...femaleOnly];
    const fm = m.filter((f, mIndex) => index !== mIndex);
    setFemaleOnly(fm);
  }

  return (
    <Drawer
      title={t("branch.create_branch")}
      width={720}
      onClose={onClose}
      visible={visible}
      bodyStyle={{ paddingBottom: 80 }}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
      >
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              name="location"
              label={t("branch.location")}
              rules={[
                {
                  required: true,
                  message: t("branch.location_placeholder"),
                },
              ]}
            >
              <Input placeholder={t("branch.location_placeholder")} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item name="photo" label="Photo">
              <Upload.Dragger
                name="files"
                beforeUpload={beforeUpload}
                showUploadList
              >
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">
                  Click or drag file to this area to upload
                </p>
                <p className="ant-upload-hint"> Support for a single upload.</p>
              </Upload.Dragger>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="linkLocation"
              label={t("branch.link_location")}
              rules={[
                {
                  required: true,
                  message: t("branch.link_location_placeholder"),
                },
              ]}
            >
              <Input placeholder={t("branch.link_location_placeholder")} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              name="startTime"
              label={t("branch.startTime")}
              rules={[
                {
                  required: true,
                  message:t("branch.available_hours_placeholder"),
                }
              ]}
            >
              <TimePicker format="HH:mm" onChange={(value) => setStartTime(value)} minuteStep={30}/>
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              name="endTime"
              label={t("branch.endTime")}
              rules={[
                {
                  required: true,
                  message:t("branch.available_hours_placeholder"),
                }
              ]}
            >
              <TimePicker format="HH:mm"  onChange={(value) => setEndTime(value)} minuteStep={30} />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <p>Female only time</p>
          </Col>
        </Row>
        
        {femaleOnly.map((f, index) => (
          <Row gutter={24} style={{ marginBottom : 10}}>
            <Col span={12}>
              <Select
                value={f.day}
                onChange={(value) => onChangeFemaleDay(value, index)}
                style={{width: "100%"}}
              >
                <Option value="">Select a day for female</Option>
                <Option value="1">{t("branch.female_only_day_mon")}</Option>
                <Option value="2">{t("branch.female_only_day_tue")}</Option>
                <Option value="3">{t("branch.female_only_day_wed")}</Option>
                <Option value="4">{t("branch.female_only_day_thr")}</Option>
                <Option value="5">{t("branch.female_only_day_fri")}</Option>
                <Option value="6">{t("branch.female_only_day_sat")}</Option>
                <Option value="0">{t("branch.female_only_day_sun")}</Option>
              </Select>
            </Col>
            <Col span={5}>
              <TimePicker 
                format="HH:mm" 
                placeholder="Start Time" 
                style={{width : "100%"}} 
                onChange={(value) => onChangeFemaleDayStartTime(value, index)}
                disabledHours={disabledHoursForFemaleOnly}
                minuteStep={30}
              />
            </Col>
            <Col span={5}>
              <TimePicker
                format="HH:mm" 
                placeholder="End Time"
                style={{width : "100%"}}
                onChange={(value) => onChangeFemaleDayEndTime(value, index)} 
                disabledHours={disabledHoursForFemaleOnly}
                minuteStep={30}
              />
            </Col>
            <Col span={2}>
              <Button 
                danger 
                type="primary" 
                shape="circle" 
                icon={<DeleteOutlined />} 
                size="small" 
                onClick={() => removeFemaleOnly(index)}
              />
            </Col>
          </Row>
        ))}

        <Row gutter={24}>
          <Col span={24}>
            <Button style={{ width : "100%"}} onClick={onAddFemaleDay}>Add Female Day</Button>
          </Col>
        </Row>

        <Row gutter={16} style={{ marginTop : 10}}>
          <Col span={24}>
            <Form.Item
              name="services"
              label={t("branch.services")}
              rules={[
                {
                  required: true,
                  message: t("branch.services_placeholder"),
                },
              ]}
            >
              <Input.TextArea
                rows={4}
                placeholder={t("branch.services_placeholder")}
              />
            </Form.Item>
          </Col>
        </Row>
        <DrawerFooter>
          <Button onClick={onClose} style={{ marginRight: 8 }}>
            {t("common.cancel")}
          </Button>
          <Button type="primary" htmlType="submit" loading={loading}>
            {t("common.submit")}
          </Button>
        </DrawerFooter>
      </Form>
    </Drawer>
  );
}
